import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Panel } from "@realtyflows/ui";
import { resetPassword } from "../api";
import Alert from "../components/Alert";
import PasswordInput from "../components/PasswordInput";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

const passwordRules = [
  { test: p => p.length >= 8, label: "At least 8 characters" },
  { test: p => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: p => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: p => /[0-9]/.test(p), label: "One number" },
  { test: p => /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?]/.test(p), label: "One special character" },
];

function PasswordStrength({ password }) {
  if (!password) return null;
  const passed = passwordRules.filter(r => r.test(password)).length;
  const strength = passed <= 2 ? "Weak" : passed <= 4 ? "Medium" : "Strong";
  const color = passed <= 2 ? t.coral : passed <= 4 ? t.amber : t.emerald;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= passed ? color : t.border, transition: "background 0.2s" }} />
        ))}
      </div>
      <div style={{ fontSize: 10, color, fontWeight: 700, fontFamily: font.mono, marginBottom: 6 }}>{strength.toUpperCase()}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
        {passwordRules.map(r => {
          const ok = r.test(password);
          return (
            <div key={r.label} style={{ fontSize: 10, color: ok ? t.emerald : t.textMuted, fontFamily: font.body, display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 8 }}>{ok ? "✓" : "○"}</span> {r.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const { isCompact } = responsive;

  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const errorRef = useScrollToAlert(error);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const allRulesPass = passwordRules.every(r => r.test(password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) { setError("Invalid reset link. Please request a new one."); return; }
    if (!allRulesPass) { setError("Password does not meet all requirements"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "16px" : "20px" }}>
      <div className="rf-a1" style={{ width: isMobile ? "100%" : 420, maxWidth: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <img src="/logo.png" alt="RealtyFlows" style={{ width: 28, height: 28, borderRadius: 4 }} />
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>
              Realty<span style={{ color: t.gold }}>Flows</span>
            </span>
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, fontFamily: font.body }}>Create a new password</div>
        </div>

        <Panel padding={isMobile ? 20 : isCompact ? 24 : 32}>
          {success ? (
            <div style={{ textAlign: "center", padding: isMobile ? "12px 0" : "20px 0" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: t.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 20, color: t.emerald }}>&#10003;</div>
              <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginBottom: 8 }}>Password Reset!</div>
              <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, marginBottom: 20 }}>
                Your password has been changed successfully.
              </div>
              <Button variant="primary" size="lg" fullWidth onClick={() => navigate("/login")}>Sign In</Button>
            </div>
          ) : !token ? (
            <div style={{ textAlign: "center", padding: isMobile ? "12px 0" : "20px 0" }}>
              <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: t.coral, fontFamily: font.body, marginBottom: 8 }}>Invalid Reset Link</div>
              <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, marginBottom: 20 }}>
                This reset link is invalid or has expired. Please request a new one.
              </div>
              <Button variant="primary" size="lg" fullWidth onClick={() => navigate("/forgot-password")}>Request New Link</Button>
            </div>
          ) : (
            <>
              <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 14 }}>
                  <PasswordInput label="New Password" value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 8 chars, upper, lower, number, special" />
                  <PasswordStrength password={password} />
                </div>
                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                  <PasswordInput label="Confirm Password" value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password" />
                  {confirm && password !== confirm && (
                    <div style={{ fontSize: 11, color: t.coral, marginTop: 4, fontFamily: font.body }}>Passwords do not match</div>
                  )}
                </div>
                <Button variant="primary" size="lg" fullWidth type="submit" disabled={loading || !allRulesPass || password !== confirm}>
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
